import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "../../style/ToastNotifications.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FaCheckCircle, FaBox, FaFileInvoice } from "react-icons/fa";
import {
  FiUser,
  FiMapPin,
  FiCalendar,
  FiTruck,
  FiPackage,
  FiSearch,
  FiPhone,
  FiMail,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
  FiHash,
  FiMessageSquare,
  FiCamera,
  FiMap,
  FiX,
} from "react-icons/fi";
import "../../style/DeliveredChallan.css";
import api from "../../api/axiosInstance";
import { TokenManager } from "../../utils/tokenManager";
import InsertProduct from "../../components/InsertProduct";
import { getAllGoods } from "../../api/goodsApi";
import { searchCustomers } from "../../api/customerApi";
import { getSrNos } from "../../api/cylinderMasterApi";
import ProductRow from "../../components/ProductRow";
import ChallanProductsSection from "../../components/ChallanProductsSection";
import { challanApi } from "../../api/challanApi";
import { BASE_URL, API_ENDPOINTS } from "../../config/apiconfig";
import { validateChallanItemSrCounts } from "../../utils/challanSrValidation";
import { getErrorMessage } from "../../utils/errorHandler";
import ChallanInvoicePopup from "../Invoices/ChallanInvoicePopup";
import { settingsApi } from "../../api/settingsApi";


// Move ProductTable outside component to prevent re-mount on every render
const ProductTable = React.memo(({ prefix, pageType, products, onUpdate, onRemove, goods, deliveredChallans, customerId }) => {
  // Add state for product search dropdowns
  const [productSearchTerms, setProductSearchTerms] = useState({});
  const [showProductDropdowns, setShowProductDropdowns] = useState({});
  const [filteredGoods, setFilteredGoods] = useState({});
  // State for multi-select Sr Nos dropdowns
  const [availableDeliveredSrs, setAvailableDeliveredSrs] = useState({});
  const [availableReceivedSrs, setAvailableReceivedSrs] = useState({});

  // Initialize filtered goods with all goods when goods prop changes
  useEffect(() => {
    if (goods && goods.length > 0) {
      const initialFiltered = {};
      products.forEach((_, index) => {
        initialFiltered[index] = goods;
      });
      setFilteredGoods(initialFiltered);
    }
  }, [goods, products]);

  const handleProductSearch = (index, searchTerm) => {
    setProductSearchTerms(prev => ({ ...prev, [index]: searchTerm }));

    const filtered = goods.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredGoods(prev => ({ ...prev, [index]: filtered }));
    setShowProductDropdowns(prev => ({ ...prev, [index]: true }));
  };

  const handleProductSelect = async (index, selectedGood) => {
    // Check if product already exists in other rows
    const existingProductIndex = products.findIndex((p, i) => i !== index && p.name === selectedGood.name);
    if (existingProductIndex !== -1) {
      toast.warning("Product already added in another row");
      return;
    }
    
    onUpdate(index, 'name', selectedGood.name);
    onUpdate(index, 'goodsItemId', selectedGood.id);
    // Auto-fill type if available from goods data
    if (selectedGood && selectedGood.type) {
      onUpdate(index, 'type', selectedGood.type);
    }
    
    setProductSearchTerms(prev => ({ ...prev, [index]: selectedGood.name }));
    setShowProductDropdowns(prev => ({ ...prev, [index]: false }));
    // Reset filtered goods to all goods
    setFilteredGoods(prev => ({ ...prev, [index]: goods }));
    
    // Fetch cylinder data when product is selected
    try {
      const cylinderName = selectedGood.name; 
      const response = await getSrNos(cylinderName, "IN_STOCK");
      console.log("Cylinder data fetched:", response.data);
      
      // Store cylinder data for this product row
      if (response.data && Array.isArray(response.data)) {
        onUpdate(index, 'cylinderData', response.data);
        // Auto-select the first batch and srNo
        if (response.data.length > 0) {
          onUpdate(index, 'selectedBatch', response.data[0].batchNo);
          onUpdate(index, 'selectedSrno', response.data[0].srNo);
        }
      }
    } catch (error) {
      console.error("Error fetching cylinder data:", error);
      toast.error("Failed to fetch cylinder data");
    }

    fetchDeliveredSrs(index, selectedGood.name);
    fetchReceivedSrs(index, selectedGood.name);
  };

  /**
   * Fetch available serial numbers for delivery (IN_STOCK status)
   */
  const fetchDeliveredSrs = async (index, productName) => {
    if (!productName) return;
    try {
      const response = await getSrNos(productName, "IN_STOCK");
      if (response.data && Array.isArray(response.data)) {
        setAvailableDeliveredSrs(prev => ({
          ...prev,
          [index]: response.data
        }));
      }
    } catch (error) {
      console.error("Error fetching delivered SRs:", error);
      toast.error("Failed to fetch available cylinders");
    }
  };

  /**
   * Fetch available serial numbers for receipt (ISSUED status)
   */
  const fetchReceivedSrs = async (index, productName) => {
    if (!productName) return;
    try {
      const response = await getSrNos(productName, "OUT_WITH_CUSTOMER", customerId);
      if (response.data && Array.isArray(response.data)) {
        setAvailableReceivedSrs(prev => ({
          ...prev,
          [index]: response.data
        }));
      }
    } catch (error) {
      console.error("Error fetching received SRs:", error);
      toast.error("Failed to fetch issued cylinders");
    }
  };

  return (
    <>
      {products.map((product, index) => (
        <ProductRow
          key={product.id}
          mode="combined"
          product={product}
          index={index}
          productsCount={products.length}
          productSearchTerm={productSearchTerms[index]}
          filteredGoods={filteredGoods[index]}
          goods={goods}
          productDropdownOpen={showProductDropdowns[index]}
          onProductSearch={handleProductSearch}
          onProductDropdownOpen={(rowIndex, open) => setShowProductDropdowns(prev => ({ ...prev, [rowIndex]: open }))}
          onProductSelect={handleProductSelect}
          onUpdate={onUpdate}
          onRemove={onRemove}
          availableDeliveredSrs={availableDeliveredSrs[index] || []}
          availableReceivedSrs={availableReceivedSrs[index] || []}
        />
      ))}
    </>
  );
});

function CombinedChallan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [showExtendedProductForm, setShowExtendedProductForm] = useState(false);
  const extendedFormRef = React.useRef(null);

  // State for collapsible sections - using tab-like selection
  const [activeSection, setActiveSection] = useState(null); // 'transport', 'delivery', 'deposit', or null
  const [showDepositDropdown, setShowDepositDropdown] = useState(false);

  const [form, setForm] = useState({
    challanNumber: "",
    challanSequence: "",
    customer: "",
    customerId: "",
    challanType: "COMBINED",
    date: new Date().toISOString().split("T")[0],
    siteLocation: "",
    vehicleNumber: "",
    transporter: "",
    driverName: "",
    driverNumber: "",
    deliveryDetails: "",
    purchaseOrderNo: "",
    depositType: "Deposit",
    returnedAmount: 0.0,
    depositeNarration: "",
    products: [{ id: Date.now(), name: "", type: "", deliveredQty: "", receivedQty: "", srNo: "", goodsItemId: null, deliveredSrNos: [], receivedSrNos: [], cylinderData: [], selectedBatch: "", selectedSrno: "" }],
    images: [],
    geoLocation: null,
  });
  const displayReturnedAmount =
    form.depositType === "Returned" && form.returnedAmount !== ""
      ? `-${String(form.returnedAmount).replace(/^-/, "")}`
      : form.returnedAmount;

  // State for tracking field errors
  const [fieldErrors, setFieldErrors] = useState({
    customer: false,
    siteLocation: false,
    products: false,
  });

  const [showInvoicePopup, setShowInvoicePopup] = useState(false);

  // Auto-increment tracking
  const [lastSavedSequence, setLastSavedSequence] = useState(null);

  // Settings state
  const [settings, setSettings] = useState({
    enableChallanImageUpload: false,
    enableChallanGeoLocation: false,
    showInvoicePopupAfterChallan: true,
    useManualChallan: false,
    invoicePrefix: '',
    challanNumberFormat: '',
    challanSequence: '',
    manualChallanStart: '',
  });

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await settingsApi.getSettings();
        if (res.status === 204) return;
        const data = res.data;
        setSettings({
          enableChallanImageUpload: data.enableChallanImageUpload || false,
          enableChallanGeoLocation: data.enableChallanGeoLocation || false,
          showInvoicePopupAfterChallan: data.showInvoicePopupAfterChallan !== false,
          useManualChallan: data.useManualChallan || false,
          invoicePrefix: data.invoicePrefix || '',
          challanNumberFormat: data.challanNumberFormat || '',
          challanSequence: data.challanSequence || '',
          manualChallanStart: data.manualChallanStart || '',
        });
      } catch (error) {
        console.error("Failed to load settings:", error);
      }
    };
    loadSettings();
  }, []);

  // Image upload state
  const [imagePreviews, setImagePreviews] = useState([]);

  // Geo location state
  const [geoLocation, setGeoLocation] = useState(null);
  const [fetchingLocation, setFetchingLocation] = useState(false);

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      toast.error('Please upload only image files');
      return;
    }

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setImagePreviews(prev => [...prev, base64]);
        setForm(prev => ({
          ...prev,
          images: [...prev.images, base64]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove image
  const handleRemoveImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Fetch geo location
  const handleFetchGeoLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        setGeoLocation(location);
        setForm(prev => ({
          ...prev,
          geoLocation: location
        }));
        setFetchingLocation(false);
        toast.success('Location captured successfully');
      },
      (error) => {
        console.error('Error getting location:', error);
        toast.error('Failed to get location. Please enable location services.');
        setFetchingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Customer search state
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.deposit-dropdown')) {
        setShowDepositDropdown(false);
      }
      if (!event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Goods state
  const [goods, setGoods] = useState([]);

  // Delivered challan data state
  const [deliveredChallans, setDeliveredChallans] = useState([]);
  const [saving, setSaving] = useState(false);

  // Fetch goods from API
  useEffect(() => {
    const fetchGoods = async () => {
      try {
        const response = await getAllGoods();
        console.log("CombinedChallan - Goods API response:", response);
        setGoods(response.data?.data || response.data || []);
      } catch (error) {
        console.error("CombinedChallan - Error fetching goods:", error);
        toast.error("Failed to load products");
      }
    };
    fetchGoods();
  }, []);

  // Fetch next challan number on mount (only for new challans)
  useEffect(() => {
    const fetchNextChallanNumber = async () => {
      if (id) return; // Don't fetch if editing existing challan

      try {
        // If manual challan is enabled, show only the sequence number
        if (settings.useManualChallan) {
          const manualNumber = settings.manualChallanStart;
          console.log("Manual challan sequence:", manualNumber);
          setForm(prev => ({
            ...prev,
            challanSequence: manualNumber.toString(),
            challanNumber: ""
          }));
        } else {
          // If manual challan is disabled, generate full challan number from settings
          const prefix = settings.invoicePrefix;
          const format = settings.challanNumberFormat;
          const sequence = settings.challanSequence;
          const challanNumber = `${prefix}/C/${format}/${sequence}`;
          console.log("Auto challan number:", challanNumber);
          setForm(prev => ({
            ...prev,
            challanNumber: challanNumber,
            challanSequence: ""
          }));
        }
      } catch (error) {
        console.error("Error fetching next challan number:", error);
      }
    };
    fetchNextChallanNumber();
  }, [id, settings]);

  // Auto-increment manual challan sequence after successful save
  useEffect(() => {
    if (settings.useManualChallan && !id && form.challanSequence && !saving && lastSavedSequence !== null) {
      const currentSequence = parseInt(form.challanSequence, 10);
      if (!isNaN(currentSequence) && currentSequence === lastSavedSequence) {
        const nextSequence = currentSequence + 1;
        console.log("Next manual challan sequence:", nextSequence);
        setForm(prev => ({
          ...prev,
          challanSequence: nextSequence.toString()
        }));
        setLastSavedSequence(null);
      }
    }
  }, [saving, lastSavedSequence, form.challanSequence, id, settings.useManualChallan]);

  // Fetch challan data for editing (same API as ChallanForm)
  useEffect(() => {
    const fetchChallanData = async () => {
      if (!id) return;

      try {
        console.log("Fetching combined challan data for ID:", id);
        const response = await api.get(`challans/combined/${id}`);
        console.log("API response:", response);
        const challanData = response.data;
        console.log("Challan data:", challanData);

        // Populate form fields with challan data (same mapping as ChallanForm)
        if (challanData.customerId) {
          setForm(prev => ({
            ...prev,
            customer: challanData.customerName,
            customerId: challanData.customerId
          }));
          setCustomerSearchTerm(challanData.customerName);
        }
        if (challanData.challanNumber) {
          setForm(prev => ({
            ...prev,
            challanNumber: challanData.challanNumber
          }));
        }
        if (challanData.challanType) {
          setForm(prev => ({
            ...prev,
            challanType: challanData.challanType
          }));
        }
        if (challanData.date) {
          setForm(prev => ({
            ...prev,
            date: challanData.date ? challanData.date.split("T")[0] : challanData.date
          }));
        }
        if (challanData.siteLocation) {
          setForm(prev => ({
            ...prev,
            siteLocation: challanData.siteLocation
          }));
        }
        if (challanData.vehicleNumber) {
          setForm(prev => ({
            ...prev,
            vehicleNo: challanData.vehicleNumber
          }));
        }
        if (challanData.transporter) {
          setForm(prev => ({
            ...prev,
            transporter: challanData.transporter
          }));
        }
        if (challanData.driverName) {
          setForm(prev => ({
            ...prev,
            driverDetails: challanData.driverName
          }));
        }
        if (challanData.deliveryDetails) {
          setForm(prev => ({
            ...prev,
            deliveryDetails: challanData.deliveryDetails
          }));
        }
        if (challanData.purchaseOrderNo) {
          setForm(prev => ({
            ...prev,
            poNo: challanData.purchaseOrderNo
          }));
        }
        if (challanData.depositeNarration) {
          setForm(prev => ({
            ...prev,
            depositeNarration: challanData.depositeNarration
          }));
        }
        if (challanData.depositeAmount !== undefined) {
          setForm(prev => ({
            ...prev,
            depositAmount: challanData.depositeAmount
          }));
        }
        if (challanData.returnedAmount !== undefined) {
          setForm(prev => ({
            ...prev,
            returnedAmount: challanData.returnedAmount
          }));
        }

        // Populate products
        if (challanData.items && challanData.items.length > 0) {
          const mappedProducts = challanData.items.map((item, index) => ({
            id: Date.now() + index,
            name: item.name || "",
            type: item.type || "",
            deliveredQty: item.deliveredQty || 0,
            receivedQty: item.receivedQty || 0,
            srNo: item.srNo || [],
            deliveredSrNos: item.deliveredSrNos || [],
            receivedSrNos: item.receivedSrNos || [],
            goodsItemId: item.goodsItemId || null,
            cylinderData: [],
            selectedBatch: "",
            selectedSrno: ""
          }));
          setForm(prev => ({
            ...prev,
            products: mappedProducts
          }));
          console.log("Mapped products:", mappedProducts);
        }
      } catch (error) {
        console.error("Error fetching challan data:", error);
        toast.error("Failed to load challan data");
      }
    };

    fetchChallanData();
  }, [id]);

  // Fetch customers using searchCustomers API
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await searchCustomers({ name: "" });
        console.log("CombinedChallan - Customers response:", response);
        const customerData = response.data?.data || response.data || [];
        setCustomers(customerData);
        setFilteredCustomers(customerData);
      } catch (error) {
        console.error("CombinedChallan - Error fetching customers:", error);
        try {
          const response = await searchCustomers({ name: "a" });
          const customerData = response.data?.data || response.data || [];
          setCustomers(customerData);
          setFilteredCustomers(customerData);
        } catch (err) {
          console.error("CombinedChallan - Fallback error:", err);
          toast.error("Failed to load customers");
        }
      }
    };
    fetchCustomers();
  }, []);

  // Customer search functions
  const handleCustomerSearch = async (searchTerm) => {
    setCustomerSearchTerm(searchTerm);

    // Filter customers based on search term
    const filtered = customers.filter(customer =>
      (customer.name || customer.customerName || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCustomers(filtered);
    setShowCustomerDropdown(true);
  };

  const handleCustomerSelect = async (customer) => {
    setForm(prev => ({
      ...prev,
      customer: customer.name || customer.customerName,
      customerId: customer.id
    }));
    setCustomerSearchTerm(customer.name || customer.customerName);
    setFieldErrors(prev => ({ ...prev, customer: false }));
    setShowCustomerDropdown(false);
    setFilteredCustomers(customers);
    setDeliveredChallans([]);
    
    // Fetch delivered challans for this customer
    fetchDeliveredChallans(customer.id);
    
    // Fetch customer inventory items
    await fetchCustomerInventory(customer.id);
  };

  // Fetch delivered challans for customer
  const fetchDeliveredChallans = async (customerId) => {
    if (!customerId) return;
    
    try {
      const token = TokenManager.getToken();
      
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.CHALLANS.SEARCH}?customerId=${customerId}&challanType=DELIVERED`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const challans = data.content || data || [];
        setDeliveredChallans(challans);
        console.log("ReceivedChallan - Delivered challans for customer:", data);
        console.log("ReceivedChallan - Challans array:", challans);
        console.log("ReceivedChallan - Number of challans:", challans.length);
        
        // Debug: Log items structure
        challans.forEach((challan, index) => {
          console.log(`Challan ${index}:`, challan);
          if (challan.items) {
            console.log(`Challan ${index} items:`, challan.items);
            challan.items.forEach((item, itemIndex) => {
              console.log(`Challan ${index} Item ${itemIndex}:`, item);
            });
          }
        });
      } else {
        console.error("Failed to fetch delivered challans");
        setDeliveredChallans([]);
      }
    } catch (error) {
      console.error("Error fetching delivered challans:", error);
      setDeliveredChallans([]);
    }
  };

  // Fetch customer inventory items for validation
  const fetchCustomerInventory = async (customerId) => {
    if (!customerId) return [];
    
    try {
      const response = await searchCustomers({ name: customerSearchTerm || form.customer || "" });
      console.log("CombinedChallan - Customer search results:", response.data);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error("Error fetching customer search results:", error);
      return [];
    }
  };

  /* RECEIVE PRODUCTS FROM INSERT PRODUCT PAGE */
  const initializedProducts = React.useRef(false);

  useEffect(() => {
    if (initializedProducts.current) return;

    if (!location.state?.products?.length) return;

    initializedProducts.current = true;

    const mapped = location.state.products.map((p) => ({
      id: Date.now() + Math.random(),
      name: p.product || "",
      type: p.type || "",
      deliveredQty: Number(p.delivered || 0),
      receivedQty: Number(p.received || 0),
      srNo: p.srNo || [],
    }));

    setForm((prev) => ({
      ...prev,
      products: mapped,
    }));

    navigate(location.pathname, {
      replace: true,
      state: {},
    });
  }, [location.state, navigate, location.pathname]);

  /* Pre-fill from Sales Order data when launched from SO → Create Challan */
  const initializedFromSO = React.useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initializedFromSO.current) return;
    const soData = location.state?.salesOrderData;
    if (!soData) return;
    initializedFromSO.current = true;

    setForm(prev => ({
      ...prev,
      customer: soData.customer || '',
      customerId: soData.customerId || '',
      poNo: soData.salesOrderNo || '',
    }));
    setCustomerSearchTerm(soData.customer || '');

    if (soData.customerId) {
      fetchCustomerInventory(soData.customerId);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

    const items = soData.items || [];
    if (items.length > 0) {
      const delivered = Number(soData.deliveredQty || 0);
      const mappedProducts = items.map((it, idx) => {
        const ordered = Number(it.qty || 0);
        const pending = Math.max(ordered - delivered, 0);
        return {
          id: Date.now() + idx,
          name: it.item || '',
          type: '',
          deliveredQty: pending > 0 ? pending : 0,
          receivedQty: 0,
          srNo: [],
          selectedSrNos: [],
          cylinderData: [],
          selectedBatch: '',
          selectedSrno: '',
          goodsItemId: idx + 1,
          _sourceSalesOrder: soData,
          _maxQty: pending,
        };
      });
      setForm(prev => ({ ...prev, products: mappedProducts }));
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, navigate, location.pathname]);

  /* Scroll to extended form when it opens */
  useEffect(() => {
    if (showExtendedProductForm && extendedFormRef.current) {
      setTimeout(() => {
        extendedFormRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [showExtendedProductForm]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }


  function handleProductsAdded(products) {
    const mappedProducts = products.map(p => ({
      name: p.product,
      deliveredQty: Number(p.delivered) || 0,
      receivedQty: Number(p.received) || 0,
      goodsItemId: p.goodsItemId,
      type: p.type || "",
      srNo: p.srNo || [],
      deliveredSrNos: [],
      receivedSrNos: [],
      cylinderData: [],
      selectedBatch: "",
      selectedSrno: ""
    }));

    setForm(prev => ({
      ...prev,
      products: [...prev.products, ...mappedProducts]
    }));

    setShowExtendedProductForm(false);
    toast.success(`${products.length} products added successfully!`);
  }

  async function handleSubmit(e, shouldRedirect = true) {
    console.log("CombinedChallan - handleSubmit called");
    e.preventDefault();
    if (saving) return;
    
    console.log("CombinedChallan - Form data:", form);
    console.log("CombinedChallan - Customer search term:", customerSearchTerm);
    console.log("CombinedChallan - Products:", form.products);
    console.log("CombinedChallan - Editing challan ID:", id);

    // Reset field errors
    const newFieldErrors = {
      customer: !form.customerId,
      siteLocation: !form.siteLocation,
      products: form.products.length === 0,
    };
    
    setFieldErrors(newFieldErrors);

    // Check for validation errors and build specific error message
    const errorMessages = [];
    if (newFieldErrors.customer) errorMessages.push("Customer");
    if (newFieldErrors.siteLocation) errorMessages.push("Site Location");
    if (newFieldErrors.products) errorMessages.push("Products");

    if (errorMessages.length > 0) {
      console.log("ReceivedChallan - Validation failed - missing fields:", errorMessages);
      toast.error(`Please fill required fields: ${errorMessages.join(', ')}`);
      return;
    }

    const token = TokenManager.getToken();
    if (!token) {
      toast.error("Session expired. Please login again.");
      navigate("/");
      return;
    }

    const payload = {
      customerId: form.customerId,
      customerName: form.customer,
      challanType: "COMBINED",
      siteLocation: form.siteLocation,
      date: form.date,
      vehicleNumber: form.vehicleNumber || "",
      transporter: form.transporter || "",
      driverName: form.driverName || "",
      driverNumber: form.driverNumber || "",
      purchaseOrderNo: form.purchaseOrderNo || "",
      deliveryDetails: form.deliveryDetails || "",
      depositType: form.depositType,
      depositeType: form.depositType,
      depositAmount: Number(form.returnedAmount) || 0,
      depositeAmount: Number(form.returnedAmount) || 0,
      depositInformation: form.depositeNarration || "",
      depositeInformation: form.depositeNarration || "",
      depositeNarration: form.depositeNarration || "",
      returnedAmount: form.depositType === "Returned" ? -Math.abs(Number(form.returnedAmount) || 0) : Number(form.returnedAmount) || 0,
      challanNumber: settings.useManualChallan
        ? form.challanSequence
        : form.challanNumber,

      sourceSalesOrderId: form.products[0]?._sourceSalesOrder?.id || null,
      sourceSalesOrderNumber: form.products[0]?._sourceSalesOrder?.salesOrderNo || null,

      items: form.products
        .filter((p) => (p.receivedQty || p.deliveredQty) > 0)
        .map((p, idx) => {
          const deliveredArray = Array.isArray(p.deliveredSrNos)
            ? p.deliveredSrNos
            : (p.deliveredSrNos ? p.deliveredSrNos.split('\n').map(s => s.trim()).filter(Boolean) : []);
          const receivedArray = Array.isArray(p.receivedSrNos)
            ? p.receivedSrNos
            : (p.receivedSrNos ? p.receivedSrNos.split('\n').map(s => s.trim()).filter(Boolean) : []);

          return {
            name: p.name,
            type: p.type,
            deliveredQty: Number(p.deliveredQty) || 0,
            receivedQty: Number(p.receivedQty) || 0,
            deliveredSrNos: deliveredArray,
            receivedSrNos: receivedArray,
            goodsItemId: p.goodsItemId || goods.find(g => g.name === p.name)?.id || idx + 1,
            batchNo: p.selectedBatch ? [p.selectedBatch] : []
          };
        }),
    };

    const srCountError = validateChallanItemSrCounts(payload.items);
    if (srCountError) {
      toast.error(srCountError);
      return;
    }

    try {
      setSaving(true);
      console.log("ReceivedChallan - Submitting payload:", payload);
      
      // Validate that all products have required fields
      const invalidProducts = payload.items.filter(item => !item.name || !item.goodsItemId);
      if (invalidProducts.length > 0) {
        toast.error("Please ensure all products have valid names and are selected from the dropdown");
        return;
      }
      
      // Validate that at least one product has delivered or received quantity
      const productsWithQty = payload.items.filter(item => item.deliveredQty > 0 || item.receivedQty > 0);
      if (productsWithQty.length === 0) {
        toast.error("Please enter delivered or received quantity for at least one product");
        return;
      }

      for (let item of payload.items) {
        // Validate delivered serial numbers
        if (item.deliveredQty > 0 && item.deliveredSrNos && item.deliveredSrNos.length > 0) {
          // Check for duplicates in delivered serials
          const deliveredSet = new Set(item.deliveredSrNos);
          if (deliveredSet.size !== item.deliveredSrNos.length) {
            toast.error(`${item.name}: Duplicate delivered serial numbers found`);
            return;
          }
        }

        // Validate received serial numbers
        if (item.receivedQty > 0 && item.receivedSrNos && item.receivedSrNos.length > 0) {
          // Check for duplicates in received serials
          const receivedSet = new Set(item.receivedSrNos);
          if (receivedSet.size !== item.receivedSrNos.length) {
            toast.error(`${item.name}: Duplicate received serial numbers found`);
            return;
          }
        }

        // Check for duplicates between delivered and received
        if (item.deliveredSrNos && item.receivedSrNos) {
          const allSerials = new Set([...item.deliveredSrNos, ...item.receivedSrNos]);
          if (allSerials.size !== (item.deliveredSrNos.length + item.receivedSrNos.length)) {
            toast.error(`${item.name}: Serial numbers are duplicated between delivered and received`);
            return;
          }
        }
      }
      
      let response;
      if (id) {
        // Update existing combined challan
        console.log("CombinedChallan - Making API call to:", `challans/combined/${id}`);
        console.log("CombinedChallan - Full URL:", `${BASE_URL}/challans/combined/${id}`);
        console.log("CombinedChallan - Payload being sent:", JSON.stringify(payload, null, 2));
        response = await api.put(`challans/combined/${id}`, payload);
      } else {
        // Create new combined challan
        console.log("CombinedChallan - Making API call to:", "challans/create-combined");
        console.log("CombinedChallan - Full URL:", `${BASE_URL}/challans/create-combined`);
        console.log("CombinedChallan - Payload being sent:", JSON.stringify(payload, null, 2));
        response = await challanApi.createCombinedChallan(payload);
      }
      
      console.log("CombinedChallan - API Response status:", response.status);
      console.log("CombinedChallan - API Response data:", response.data);
      console.log("CombinedChallan - Full API Response:", response);
      
      if (response.status === 201 || response.status === 200) {
        // Update Sales Order deliveredQty if this challan was created from a SO
        const soRef = form.products[0]?._sourceSalesOrder;
        if (soRef?.id) {
          try {
            const soList = JSON.parse(localStorage.getItem('salesOrders') || '[]');
            const totalDeliveredInThisChallan = payload.items.reduce((s, it) => s + Number(it.deliveredQty || 0), 0);
            const updatedSOs = soList.map(so => {
              if (String(so.id) === String(soRef.id)) {
                const newDelivered = Number(so.deliveredQty || 0) + totalDeliveredInThisChallan;
                const totalOrdered = (so.items || []).reduce((s, it) => s + Number(it.qty || 0), 0);
                const pending = Math.max(totalOrdered - newDelivered, 0);
                let newStatus = so.status;
                if (pending <= 0 && totalOrdered > 0) newStatus = 'Delivered';
                else if (newDelivered > 0) newStatus = 'Partially Delivered';
                return { ...so, deliveredQty: newDelivered, pendingQty: pending, status: newStatus, updatedAt: new Date().toISOString() };
              }
              return so;
            });
            localStorage.setItem('salesOrders', JSON.stringify(updatedSOs));
          } catch (e) { console.error('Failed to update SO deliveredQty:', e); }
        }

        if (shouldRedirect) {
          // Show toast with 3 second auto-close, navigate after toast closes
          const message = id ? "Combined Challan updated successfully!" : "Combined Challan created successfully!";
          toast.success(message, {
            autoClose: 3000,
            onClose: () => {
              setShowInvoicePopup(false);
              navigate("/dashboard/challans/view");
            }
          });
        } else {
          const message = id ? "Combined Challan updated successfully!" : "Combined Challan created successfully!";
          toast.success(message);
          // Stay on same page
          if (settings.useManualChallan && !id) {
            // Set last saved sequence for auto-increment
            setLastSavedSequence(parseInt(form.challanSequence, 10));
          } else {
            handleReset();
          }
          // Refresh customer inventory after successful save
          if (form.customerId) {
            await fetchCustomerInventory(form.customerId);
          }

          // Trigger dashboard refresh event
          window.dispatchEvent(new CustomEvent('refreshDashboard'));
        }
      } else {
        toast.error("Unexpected response status: " + response.status);
      }
    } catch (err) {
      console.error("ReceivedChallan - API Error:", err);
      console.error("ReceivedChallan - Error Response:", err.response?.data);
      
      const errorMessage = getErrorMessage(err);
      toast.error(errorMessage);
      
      // Handle authentication errors separately
      if (err.response?.status === 401) {
        navigate("/");
      }
    } finally {
      setSaving(false);
    }
  }

  const handleReset = () => {
    setForm({
      challanNumber: "",
      challanSequence: "",
      customer: "",
      customerId: "",
      challanType: "COMBINED",
      date: new Date().toISOString().split("T")[0],
      siteLocation: "",
      vehicleNumber: "",
      transporter: "",
      driverName: "",
      driverNumber: "",
      deliveryDetails: "",
      purchaseOrderNo: "",
      depositType: "Deposit",
      returnedAmount: 0.0,
      depositeNarration: "",
      products: [],
    });
    setShowDepositDropdown(false);

    // Clear field errors
    setFieldErrors({
      customer: false,
      siteLocation: false,
      products: false,
    });
  };

  const handleProductRowChange = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      products: prev.products.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeProductRow = (index) => {
    if (form.products.length > 1) {
      const updatedProducts = form.products.filter((_, i) => i !== index);
      setForm(prev => ({ ...prev, products: updatedProducts }));
    }
  };

  return (
    <div className="dc-container">

      <div className="dc-header">
        <div className="dc-header-icon">
          <FaFileInvoice size={22} />
        </div>
      
        <div>
          <h2>Combined Challan</h2>
          <p>Create and manage challan information</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="dc-grid">
            {/* === CUSTOMER & LOCATION === */}
            <div className="dc-section">
              <div className="dc-section-title">
                <FiUser size={16} />
                Customer &amp; Location
              </div>
              <div className="dc-grid gap-20">
                <div className="dc-field full">
                  <label className="dc-field-label">
                    <FiUser size={14} />
                    <span>Customer Name *</span>
                  </label>
                  <div className="customer-search-container">
                    <div className="customer-search-input-wrapper">
                      <input
                        name="customer"
                        value={customerSearchTerm}
                        onChange={(e) => {
                          const searchTerm = e.target.value;
                          handleCustomerSearch(searchTerm);
                          // A typed value is only a search term until the user
                          // selects an actual customer from the dropdown.
                          setForm(prev => ({
                            ...prev,
                            customer: searchTerm,
                            customerId: ""
                          }));
                          // Clear error when user starts typing
                          if (fieldErrors.customer) {
                            setFieldErrors(prev => ({ ...prev, customer: false }));
                          }
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                        placeholder="Search customer..."
                        required
                        className={`customer-search-input ${fieldErrors.customer ? 'has-error' : ''}`}
                      />
                      <div className="customer-search-icon">
                        {showCustomerDropdown ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                      </div>
                    </div>
                    {fieldErrors.customer && (
                      <div className="dc-field-error customer-field-error">
                        Please select a customer from the dropdown
                      </div>
                    )}
                    {showCustomerDropdown && (
                      <div className="customer-dropdown">
                        {filteredCustomers.length === 0 ? (
                          <div className="customer-no-results">
                            <div className="no-results-icon"><FiSearch size={20} /></div>
                            <div className="no-results-text">No customers found</div>
                            <div className="no-results-hint">Try searching with different keywords</div>
                          </div>
                        ) : (
                          filteredCustomers.map((customer) => (
                            <div
                              key={customer.id}
                              className="customer-option"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                handleCustomerSelect(customer);
                              }}
                            >
                              <div className="customer-option-main">
                                <div className="customer-name">
                                  {customer.name || customer.customerName}
                                </div>
                                {customer.email && (
                                  <div className="customer-email">
                                    <FiMail size={12} /> {customer.email}
                                  </div>
                                )}
                                {customer.phone && (
                                  <div className="customer-phone">
                                    <FiPhone size={12} /> {customer.phone}
                                  </div>
                                )}
                              </div>
                              <div className="customer-select-icon"><FiCheck size={14} /></div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="dc-field full">
                  <label className="dc-field-label">
                    <FiMapPin size={14} />
                    <span>Site Location *</span>
                  </label>
                  <div className="customer-search-input-wrapper location-input">
                    <FiMapPin size={15} className="dc-input-icon" />
                    <input
                      name="siteLocation"
                      value={form.siteLocation}
                      onChange={(e) => {
                        handleChange(e);
                        // Clear error when user starts typing
                        if (fieldErrors.siteLocation) {
                          setFieldErrors(prev => ({ ...prev, siteLocation: false }));
                        }
                      }}
                      placeholder="Enter site location"
                      required
                      className={`customer-search-input ${fieldErrors.siteLocation ? 'has-error' : ''}`}
                    />
                  </div>
                  {fieldErrors.siteLocation && (
                    <div className="dc-field-error">
                      Site location is required
                    </div>
                  )}
                </div>

                {/* Geo Location Section */}
                {settings.enableChallanGeoLocation && (
                  <div className="dc-field full">
                    <label className="dc-field-label">
                      <FiMap size={14} />
                      <span>Geo Location</span>
                    </label>
                    <div className="geo-location-wrapper">
                      <button
                        type="button"
                        onClick={handleFetchGeoLocation}
                        disabled={fetchingLocation}
                        className="geo-location-button"
                      >
                        {fetchingLocation ? (
                          <>
                            <span className="loading-spinner"></span>
                            Capturing Location...
                          </>
                        ) : (
                          <>
                            <FiMap size={16} />
                            Capture Current Location
                          </>
                        )}
                      </button>
                      {geoLocation && (
                        <div className="geo-location-info">
                          <div className="geo-location-item">
                            <span className="geo-label">Latitude:</span>
                            <span className="geo-value">{geoLocation.latitude.toFixed(6)}</span>
                          </div>
                          <div className="geo-location-item">
                            <span className="geo-label">Longitude:</span>
                            <span className="geo-value">{geoLocation.longitude.toFixed(6)}</span>
                          </div>
                          <div className="geo-location-item">
                            <span className="geo-label">Accuracy:</span>
                            <span className="geo-value">{geoLocation.accuracy.toFixed(0)}m</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Image Upload Section */}
                {settings.enableChallanImageUpload && (
                  <div className="dc-field full">
                    <label className="dc-field-label">
                      <FiCamera size={14} />
                      <span>Challan Images</span>
                    </label>
                    <div className="image-upload-wrapper">
                      <input
                        type="file"
                        id="challan-image-upload"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="image-upload-input"
                      />
                      <label htmlFor="challan-image-upload" className="image-upload-button">
                        <FiCamera size={16} />
                        Upload Images
                      </label>
                      {imagePreviews.length > 0 && (
                        <div className="image-previews">
                          {imagePreviews.map((preview, index) => (
                            <div key={index} className="image-preview-item">
                              <img src={preview} alt={`Preview ${index + 1}`} />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                className="image-remove-button"
                              >
                                <FiX size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* === BASIC INFO === */}
            <div className="dc-section">
              <div className="dc-section-title">
                <FiCalendar size={16} />
                Basic Information
              </div>
              <div className="dc-flex-column gap-20">
                <div className="dc-field full">
                  <label className="dc-field-label">
                    <FiCalendar size={14} />
                    <span>Date *</span>
                  </label>
                  <div className="customer-search-input-wrapper date-input">
                    <input
                      name="date"
                      type="date"
                      value={form.date}
                      onChange={handleChange}
                      required
                      className="customer-search-input"
                    />
                  </div>
                </div>

                <div className="dc-grid two-columns gap-20">
                  <div className="dc-info-field">
                    <label className="dc-field-label">
                      <FiFileText size={14} />
                      <span>Challan Type</span>
                    </label>
                    <div className="dc-info-value combined">
                      {form.challanType}
                    </div>
                  </div>

                  <div className="dc-info-field">
                    <label className="dc-field-label">
                      <FiHash size={14} />
                      <span>Challan Number</span>
                    </label>
                    <div className="dc-info-value number">
                      {settings.useManualChallan ? form.challanSequence : form.challanNumber}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* === OPTIONAL SECTIONS TABS === */}
            <div className="dc-tabs-section">
              <div className="dc-tabs-header">
                <button
                  type="button"
                  className={`dc-tab ${activeSection === 'transport' ? 'active' : ''}`}
                  onClick={() => setActiveSection(activeSection === 'transport' ? null : 'transport')}
                >
                  <FiTruck size={16} />
                  <span>Transport Details</span>
                </button>
                <button
                  type="button"
                  className={`dc-tab ${activeSection === 'delivery' ? 'active' : ''}`}
                  onClick={() => setActiveSection(activeSection === 'delivery' ? null : 'delivery')}
                >
                  <FiPackage size={16} />
                  <span>Delivery Information</span>
                </button>
                <button
                  type="button"
                  className={`dc-tab ${activeSection === 'deposit' ? 'active' : ''}`}
                  onClick={() => setActiveSection(activeSection === 'deposit' ? null : 'deposit')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-currency-rupee" viewBox="0 0 16 16">
                    <path d="M4 3.06h2.726c1.22 0 2.12.575 2.325 1.724H4v1.051h5.051C8.855 7.001 8 7.558 6.788 7.558H4v1.317L8.437 14h2.11L6.095 8.884h.855c2.316-.018 3.465-1.476 3.688-3.049H12V4.784h-1.345c-.08-.778-.357-1.335-.793-1.732H12V2H4z"/>
                  </svg>
                  <span>Deposit Information</span>
                </button>
              </div>

              {/* TRANSPORT DETAILS CONTENT */}
              {activeSection === 'transport' && (
                <div className="dc-tab-content">
                  <div className="dc-grid two-columns gap-20">
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiTruck size={14} />
                        <span>Vehicle Number</span>
                      </label>
                      <input name="vehicleNumber" value={form.vehicleNumber} onChange={handleChange} placeholder="Enter vehicle number" />
                    </div>
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiTruck size={14} />
                        <span>Transporter</span>
                      </label>
                      <input name="transporter" value={form.transporter} onChange={handleChange} placeholder="Enter transporter details" />
                    </div>
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiUser size={14} />
                        <span>Driver Name</span>
                      </label>
                      <input name="driverName" value={form.driverName} onChange={handleChange} placeholder="Enter driver name" />
                    </div>
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiPhone size={14} />
                        <span>Driver Number</span>
                      </label>
                      <input name="driverNumber" value={form.driverNumber} onChange={handleChange} placeholder="Enter driver number" />
                    </div>
                  </div>
                </div>
              )}

              {/* DELIVERY INFORMATION CONTENT */}
              {activeSection === 'delivery' && (
                <div className="dc-tab-content">
                  <div className="dc-grid two-columns gap-20">
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiPackage size={14} />
                        <span>Delivery Details</span>
                      </label>
                      <input name="deliveryDetails" value={form.deliveryDetails} onChange={handleChange} placeholder="Enter delivery details" />
                    </div>
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiFileText size={14} />
                        <span>PO No.</span>
                      </label>
                      <input name="purchaseOrderNo" value={form.purchaseOrderNo} onChange={handleChange} placeholder="Enter PO number" />
                    </div>
                  </div>
                </div>
              )}

              {/* DEPOSIT INFORMATION CONTENT */}
              {activeSection === 'deposit' && (
                <div className="dc-tab-content">
                  <div className="dc-grid two-columns gap-20">
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-currency-rupee" viewBox="0 0 16 16">
                          <path d="M4 3.06h2.726c1.22 0 2.12.575 2.325 1.724H4v1.051h5.051C8.855 7.001 8 7.558 6.788 7.558H4v1.317L8.437 14h2.11L6.095 8.884h.855c2.316-.018 3.465-1.476 3.688-3.049H12V4.784h-1.345c-.08-.778-.357-1.335-.793-1.732H12V2H4z"/>
                        </svg>
                        <span>Deposit Type</span>
                      </label>
                      <div className="deposit-dropdown">
                        <div
                          className="customer-search-input-wrapper"
                          onClick={() => setShowDepositDropdown(!showDepositDropdown)}
                        >
                          <input
                            readOnly
                            value={form.depositType || "Deposit"}
                            className="customer-search-input"
                            onBlur={() => setTimeout(() => setShowDepositDropdown(false), 200)}
                          />
                          <div className="customer-search-icon">
                            {showDepositDropdown ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                          </div>
                        </div>
                        {showDepositDropdown && (
                          <div className="customer-dropdown">
                            {["Deposit", "Returned"].map((option) => (
                              <div
                                key={option}
                                className={`customer-option ${form.depositType === option ? 'selected' : ''}`}
                                onMouseDown={() => {
                                  setForm({
                                    ...form,
                                    depositType: option
                                  });
                                  setShowDepositDropdown(false);
                                }}
                              >
                                <div className="customer-option-main">
                                  <div className="customer-name">
                                    {option}
                                  </div>
                                </div>
                                {form.depositType === option && (
                                  <div className="customer-select-icon">
                                    <FiCheck size={14} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="dc-field">
                      <label className="dc-field-label">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-currency-rupee" viewBox="0 0 16 16">
                          <path d="M4 3.06h2.726c1.22 0 2.12.575 2.325 1.724H4v1.051h5.051C8.855 7.001 8 7.558 6.788 7.558H4v1.317L8.437 14h2.11L6.095 8.884h.855c2.316-.018 3.465-1.476 3.688-3.049H12V4.784h-1.345c-.08-.778-.357-1.335-.793-1.732H12V2H4z"/>
                        </svg>
                        <span>{form.depositType === 'Returned' ? 'Returned Amount ()' : 'Deposit Amount ()'}</span>
                      </label>
                    <input
                      name="returnedAmount"
                      type="text"
                      value={displayReturnedAmount}
                      onChange={(e) => {
                        const nextValue = e.target.value.replace(/^-/, "");
                        setForm(prev => ({ ...prev, returnedAmount: nextValue }));
                      }}
                      placeholder="0.00"
                      className="customer-search-input"
                    />
                  </div>

                  <div className="dc-field full">
                    <label className="dc-field-label center">
                      <FiMessageSquare size={14} />
                      <span>Deposit Narration</span>
                    </label>
                    <div className="customer-search-input-wrapper">
                      <input
                        name="depositeNarration"
                        value={form.depositeNarration}
                        onChange={handleChange}
                        placeholder="Enter narration"
                        className="customer-search-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>

          <div style={{ marginTop: '16px', overflow: 'visible', width: '100%', boxSizing: 'border-box' }}>
            {fieldErrors.products && (
              <div className="dc-field-error" style={{ marginBottom: '8px' }}>
                At least one product is required
              </div>
            )}
            <ChallanProductsSection
              count={form.products.length}
              hasError={fieldErrors.products}
              subtitle="Enter delivered and received quantities together with their matching SR selections."
              onAddProduct={() => {
                const newProduct = { 
                  id: Date.now(), 
                  name: "", 
                  type: "", 
                  deliveredQty: "", 
                  receivedQty: "", 
                  srNo: "", 
                  goodsItemId: null, 
                  deliveredSrNos: [], 
                  receivedSrNos: [],
                  cylinderData: [],
                  selectedBatch: "",
                  selectedSrno: ""
                };
                setForm(prev => {
                  if (prev.products.length && prev.products[prev.products.length - 1].name === "") {
                    return prev;
                  }
                  if (fieldErrors.products) {
                    setFieldErrors(prev => ({ ...prev, products: false }));
                  }
                  return { ...prev, products: [...prev.products, newProduct] };
                });
              }}
              actions={
                <>
                  <button
                    type="button"
                    className="challan-reset-btn"
                    onClick={handleReset}
                    disabled={saving}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="challan-create-btn"
                    onClick={(e) => handleSubmit(e, false)}
                    disabled={saving}
                  >
                    {saving ? <span className="challan-create-spinner" /> : <FaCheckCircle size={15} />}
                    {saving ? "Saving..." : "Save & Continue"}
                  </button>
                  <button
                    type="button"
                    className="challan-create-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      
                      // Validate form before showing popup
                      const newFieldErrors = {
                        customer: !form.customerId,
                        siteLocation: !form.siteLocation,
                        products: form.products.length === 0,
                      };

                      setFieldErrors(newFieldErrors);

                      const errorMessages = [];
                      if (newFieldErrors.customer) errorMessages.push("Customer");
                      if (newFieldErrors.siteLocation) errorMessages.push("Site Location");
                      if (newFieldErrors.products) errorMessages.push("Products");

                      if (errorMessages.length > 0) {
                        toast.error(`Please fill the form first: ${errorMessages.join(', ')}`);
                        return;
                      }

                      // Check if invoice popup should be shown after challan creation
                      if (settings.showInvoicePopupAfterChallan) {
                        setShowInvoicePopup(true);
                      } else {
                        // Directly create challan without popup
                        handleSubmit({ preventDefault: () => {} }, true);
                      }
                    }}
                    disabled={saving}
                  >
                    {saving ? <span className="challan-create-spinner" /> : <FaCheckCircle size={15} />}
                    {saving ? "Creating..." : "Generate Challan"}
                  </button>
                </>
              }
            >
                    <ProductTable
                      prefix="dc"
                      pageType="received"
                      products={form.products}
                      onUpdate={handleProductRowChange}
                      onRemove={removeProductRow}
                      goods={goods}
                      deliveredChallans={deliveredChallans}
                      customerId={form.customerId}
                    />
            </ChallanProductsSection>
          </div>
        </form>

      {/* Extended Product Form - Inline */}
      {showExtendedProductForm && (
        <div className="dc-extended-product-section" ref={extendedFormRef}>
          <div className="dc-extended-product-header">
            <h3><FaBox /> Add Multiple Products</h3>
            <button 
              className="dc-extended-close-btn"
              onClick={() => setShowExtendedProductForm(false)}
            >
              ×
            </button>
          </div>
          <div className="rc-extended-product-content">
            {/* The closing div for dc-grid was missing, it is added below after the form's children */}
            <InsertProduct
              onProductsAdded={handleProductsAdded}
              onClose={() => setShowExtendedProductForm(false)}
              isInModal={false}
            />
          </div>
        </div>
      )}

      <ChallanInvoicePopup
        open={showInvoicePopup}
        type={form.challanType || "Challan"}
        form={form}
        products={form.products || []}
        saving={saving}
        onClose={() => setShowInvoicePopup(false)}
        onGenerate={() => {
          setShowInvoicePopup(false);
          handleSubmit({ preventDefault: () => {} }, true);
        }}
      />
    </div>
  );
}

export default CombinedChallan;

 
